import { Component, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { SignupStateService } from '../../services/signup-state.service';
import { OcrExtraction } from '../../interfaces/signup.interfaces';
import {
  COUNTRY_PHONE_OPTIONS,
  PROFESSIONS,
  GENDER_OPTIONS,
  VOLUME_OPTIONS,
  DOCUMENT_TYPE_MAP,
  PASOS_PERSONA,
} from '../../constants/signup.constants';
import { mapNacionalidad, residenciaSugerida } from '../../utils/residencia-nacionalidad';
import {
  cumpleEdadMinima,
  fechaNacimientoMaxima,
  MENSAJE_MENOR_DE_EDAD,
} from '../../../../../shared/utils/edad-minima.util';

@Component({
  selector: 'app-data-confirmation-step',
  templateUrl: './data-confirmation-step.component.html',
  styleUrls: ['./data-confirmation-step.component.sass'],
})
export class DataConfirmationStepComponent implements OnInit, OnDestroy {
  @Output() onError = new EventEmitter<string>();
  @Output() onSuccess = new EventEmitter<string>();

  dataForm!: FormGroup;
  loading = false;

  /** Tope del selector de fecha: nadie más joven que la edad mínima */
  readonly fechaNacimientoMaxima = fechaNacimientoMaxima();

  // Datos para selectores
  countries = COUNTRY_PHONE_OPTIONS;
  professions = PROFESSIONS;
  genderOptions = GENDER_OPTIONS;
  volumeOptions = VOLUME_OPTIONS;

  // Control para "Otro" en profesión
  showOtherProfession = false;

  private subscriptions: Subscription[] = [];

  constructor(
    private fb: FormBuilder,
    public signupState: SignupStateService
  ) {}

  ngOnInit(): void {
    // Verificar que existen datos OCR
    if (!this.signupState.currentState.ocrData) {
      this.signupState.goToStep(PASOS_PERSONA.OCR);
      return;
    }

    this.initForm();
    this.populateFormFromOcr();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  private initForm(): void {
    this.dataForm = this.fb.group({
      // Hidden - tipo de documento país
      documentoPais: [''],

      // Readonly (del OCR)
      nombres: [{ value: '', disabled: true }, Validators.required],
      apellidos: [{ value: '', disabled: true }, Validators.required],
      tipoDocumento: [{ value: '', disabled: true }],
      numeroSerie: [{ value: '', disabled: true }],

      // Editables
      numeroDocumento: ['', Validators.required],
      fechaNacimiento: ['', Validators.required],
      nacionalidad: ['', Validators.required],
      sexo: ['', Validators.required],
      fechaVencimiento: [''],
      profesion: ['', Validators.required],
      otraProfesion: [''],
      paisResidencia: ['', Validators.required],
      ciudadResidencia: ['', Validators.required],
      direccion: ['', Validators.required],
      volumenMensual: ['', Validators.required],
    });

    // Escuchar cambio de profesión para mostrar/ocultar campo "Otro"
    const profSub = this.dataForm.get('profesion')?.valueChanges.subscribe((value) => {
      this.showOtherProfession = value === 'Otro (especificar)';
      if (this.showOtherProfession) {
        this.dataForm.get('otraProfesion')?.setValidators([Validators.required]);
      } else {
        this.dataForm.get('otraProfesion')?.clearValidators();
        this.dataForm.get('otraProfesion')?.setValue('');
      }
      this.dataForm.get('otraProfesion')?.updateValueAndValidity();
    });
    if (profSub) this.subscriptions.push(profSub);
  }

  private populateFormFromOcr(): void {
    const ocrData = this.signupState.currentState.ocrData;
    if (!ocrData) return;

    // Determinar país del documento
    // Solo define el tipo/numero de documento: NO implica nacionalidad ni residencia
    // (un extranjero puede tener cedula chilena y un venezolano puede residir en Chile)
    const documentoPais = this.signupState.documentType; // 'chile' | 'venezuela'

    // Mapear nacionalidad del código a nombre.
    // Solo se usa lo que trajo el OCR: NO se cae al pais del documento
    // (ocrData.country es 'CHL'/'VEN', el pais que emitio el documento).
    // Si el documento no declara nacionalidad, el select queda vacio y lo
    // elige el usuario, que es requerido para continuar.
    const nacionalidadNombre = mapNacionalidad(ocrData.nationality);

    // Mapear tipo de documento
    // Chile: siempre "RUT"
    // Venezuela: usar tipoCedula (V o E) del OCR
    const tipoDocumentoLabel = documentoPais === 'chile'
      ? 'RUT'
      : ocrData.documentPrefix || 'V'; // documentPrefix = tipoCedula del OCR Venezuela

    // Obtener número de documento según país
    const numeroDocumento = documentoPais === 'chile'
      ? ocrData.RUN || ocrData.documentNumber || ''
      : ocrData.documentNumber || '';

    // Obtener número de documento sin formato (readonly)
    // Chile: runSinFormato
    // Venezuela: numeroCedulaSinFormato
    const numeroSerie = ocrData.documentNumberClean || '';

    this.dataForm.patchValue({
      // Hidden
      documentoPais: documentoPais,

      // Readonly
      nombres: ocrData.firstName || '',
      apellidos: ocrData.lastName || '',
      tipoDocumento: tipoDocumentoLabel,
      numeroSerie: numeroSerie,

      // Editables con valores del OCR
      numeroDocumento: numeroDocumento,
      fechaNacimiento: ocrData.fechaNacimientoISO || '',
      nacionalidad: nacionalidadNombre,
      sexo: ocrData.gender || '',
      fechaVencimiento: ocrData.expirationDate || '',

      // Pais de residencia: se pre-selecciona segun el pais del documento
      // (RUT chileno -> Chile, cedula venezolana -> Venezuela), NUNCA segun la
      // nacionalidad; el usuario puede corregirlo en ambos casos.
      // Si el documento no es de ninguno de los dos, no se adivina nada.
      paisResidencia: residenciaSugerida(documentoPais),

      // Campos que el usuario debe llenar
      ciudadResidencia: '',
      direccion: '',
      profesion: '',
      volumenMensual: '',
    });

    // La fecha que leyo el OCR no se edita: es la que decide la edad minima, y
    // dejarla editable permitia cambiarla por una de adulto. getRawValue() la
    // sigue leyendo aunque este deshabilitada.
    if (ocrData.fechaNacimientoISO) {
      this.dataForm.get('fechaNacimiento')?.disable();
    }
  }

  /**
   * Convierte tipo de documento a etiqueta legible
   */
  private mapDocumentType(type: string): string {
    if (!type) return '';
    return DOCUMENT_TYPE_MAP[type] || type;
  }

  /**
   * Obtiene la bandera para un país
   */
  getFlagForCountry(countryName: string): string {
    const country = this.countries.find(
      (c) => c.name.toLowerCase() === countryName?.toLowerCase()
    );
    return country ? country.flag : 'chile.png';
  }

  /**
   * Envía el formulario
   */
  async onSubmit(): Promise<void> {
    if (this.dataForm.invalid) {
      this.markFormAsTouched();
      this.onError.emit('Por favor completa todos los campos requeridos.');
      return;
    }

    // Un control deshabilitado no corre validadores, asi que la edad se revisa
    // aca. El que manda es el backend; esto solo evita seguir con el registro.
    if (!cumpleEdadMinima(this.dataForm.getRawValue().fechaNacimiento)) {
      this.onError.emit(MENSAJE_MENOR_DE_EDAD);
      return;
    }

    this.loading = true;

    try {
      // Obtener todos los valores del formulario (incluyendo disabled)
      const formData = this.dataForm.getRawValue();

      // Guardar datos confirmados en el estado
      this.saveConfirmedData(formData);

      // Avanzar al siguiente paso (PEP declaration)
      await this.signupState.nextStep();
      this.onSuccess.emit('Datos confirmados correctamente');
    } catch (err: any) {
      this.onError.emit('Error al guardar los datos. Intenta nuevamente.');
    } finally {
      this.loading = false;
    }
  }

  /**
   * Guarda los datos confirmados/editados en el estado
   */
  private saveConfirmedData(formData: any): void {
    const currentOcrData = this.signupState.currentState.ocrData!;

    // Determinar la profesión final
    const profesionFinal = formData.profesion === 'Otro (especificar)'
      ? formData.otraProfesion
      : formData.profesion;

    // Crear objeto con datos confirmados
    const confirmedData: OcrExtraction = {
      ...currentOcrData,
      // Datos del documento
      documentoPais: formData.documentoPais,
      firstName: formData.nombres,
      lastName: formData.apellidos,
      documentType: currentOcrData?.documentType || '',
      documentNumber: formData.numeroDocumento,
      RUN: formData.documentoPais === 'chile' ? formData.numeroDocumento : undefined,

      // Datos personales
      fechaNacimientoISO: formData.fechaNacimiento,
      nationality: formData.nacionalidad,
      gender: formData.sexo,
      expirationDate: formData.fechaVencimiento,

      // Datos adicionales del usuario
      profession: profesionFinal,
      countryAddress: formData.paisResidencia,
      cityAddress: formData.ciudadResidencia,
      address: formData.direccion,
      volumenMensual: formData.volumenMensual,
    };

    this.signupState.setOcrData(confirmedData);
  }

  /**
   * Marca todos los campos como touched para mostrar errores
   */
  private markFormAsTouched(): void {
    Object.keys(this.dataForm.controls).forEach((key) => {
      this.dataForm.get(key)?.markAsTouched();
    });
  }

  /**
   * Vuelve al paso anterior
   */
  async goBack(): Promise<void> {
    await this.signupState.prevStep();
  }

  // ==================== GETTERS PARA VALIDACIÓN ====================

  isFieldInvalid(fieldName: string): boolean {
    const field = this.dataForm.get(fieldName);
    return field ? field.invalid && field.touched : false;
  }

  get isFormValid(): boolean {
    return this.dataForm.valid;
  }
}
